import React, { useState, useEffect } from 'react';
import './styles.css'
import axios from 'axios';
import { baseURL } from './api/baseURL';
import { connect } from 'react-redux';
import Modal from './Modal';
import Loader from './Loader';
import moment from 'moment';
import { MDBFile, MDBBtn } from 'mdb-react-ui-kit';
import AdminShell from './components/layout/AdminShell';
import ExportButton from './components/ExportButton';
import { exportElementToPdf } from './utils/exportPagePdf';

type BookingPeriodKey = 'today' | 'week' | 'month' | 'year';
type MatchPeriodKey = 'week' | 'month' | 'year';
type PhotographerMetricKey = 'total' | 'live' | 'new' | 'removed';
type ShootTypePeriodKey = 'month' | 'quarter' | 'year';
const PREV_KEY_FOR: Record<BookingPeriodKey, string> = { today: 'yesterday', week: 'weekPrev', month: 'monthPrev', year: 'yearPrev' };
const VS_LABEL_FOR: Record<BookingPeriodKey, string> = { today: 'vs yesterday', week: 'vs last week', month: 'vs last month', year: 'vs last year' };
const MATCH_PREV_KEY_FOR: Record<MatchPeriodKey, string> = { week: 'weekPrev', month: 'monthPrev', year: 'yearPrev' };
const MATCH_VS_LABEL_FOR: Record<MatchPeriodKey, string> = { week: 'vs last week', month: 'vs last month', year: 'vs last year' };

/** Rounded-full segmented pill control — matches the client mockup's
 * Today/Weekly/Monthly/Yearly KPI-card toggle shape (single pill track,
 * solid-filled active segment) rather than this app's separate-chip style
 * used elsewhere, since the mockup calls this out as a distinct pattern. */
function SegmentedControl<T extends string>({ options, value, onChange }: { options: { key: T, label: string }[], value: T, onChange: (v: T) => void }) {
  return (
    <div style={{ display: 'inline-flex', background: 'rgba(255,255,255,0.06)', borderRadius: 999, padding: 3, gap: 2 }}>
      {options.map(o => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          style={{
            border: 'none', cursor: 'pointer', borderRadius: 999, padding: '5px 12px', fontSize: 11, fontWeight: 700,
            background: value === o.key ? '#0090e7' : 'transparent',
            color: value === o.key ? '#fff' : 'rgba(255,255,255,0.55)',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function pctDelta(curr: number, prev: number): number | null {
  if (!Number.isFinite(curr) || !Number.isFinite(prev)) return null;
  if (prev === 0) return curr === 0 ? 0 : null;
  return Math.round(((curr - prev) / prev) * 1000) / 10;
}

/** `goodDirection`: 'up' for metrics where more is better (bookings, revenue),
 * 'down' for metrics where less is better (avg match time). */
function DeltaBadge({ deltaLabel, isGood, hidden }: { deltaLabel: string | null, isGood: boolean | null, hidden?: boolean }) {
  if (hidden || deltaLabel == null) return <span style={{ fontSize: 11, opacity: 0.4 }}>No prior-period data</span>;
  const color = isGood == null ? 'rgba(255,255,255,0.5)' : isGood ? '#3ddc84' : '#ff6b6b';
  const bg = isGood == null ? 'rgba(255,255,255,0.06)' : isGood ? 'rgba(61,220,132,0.12)' : 'rgba(255,107,107,0.12)';
  return (
    <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 999, padding: '3px 9px' }}>
      {deltaLabel}
    </span>
  );
}

type Props = {
  history: any,
  isSignedIn?: string,
  email?: string,
  user_type?: string,
  token?: string,
  dispatch?: any,
};

type State = {
  requested: boolean,
  err: boolean,
  bannerRemoved: boolean,
  uploadImage: any,
  dashboardData: any,
  dashboardStats: any,
  selectedFile: any,
  showConfirmation: any,
  bookingsPeriod: BookingPeriodKey,
  revenuePeriod: BookingPeriodKey,
  matchPeriod: MatchPeriodKey,
  photographerMetric: PhotographerMetricKey,
  orderTrendPeriod: MatchPeriodKey,
  shootTypePeriod: ShootTypePeriodKey,
  exporting: boolean,
};

let tempTimeout: any = undefined;

class Home extends React.Component<Props> {

  /** Refreshes dashboard data (alerts, orders) without blocking the main loader */
  dashboardPollTimer: ReturnType<typeof setInterval> | null = null;
  orderTrendChartInstance: any = null;
  shootTypeChartInstance: any = null;


  state: State = {
    requested: false,
    err: false,
    dashboardData: null,
    dashboardStats: null,
    bannerRemoved: false,
    uploadImage: false,
    selectedFile: null,
    showConfirmation: false,
    bookingsPeriod: 'today',
    revenuePeriod: 'today',
    matchPeriod: 'week',
    photographerMetric: 'total',
    orderTrendPeriod: 'week',
    shootTypePeriod: 'year',
    exporting: false,
  }

  exportRef = React.createRef<HTMLDivElement>();

  // Screenshot-style PDF of the whole dashboard as currently shown — the
  // KPI period toggles and chart filters selected on screen are what get
  // captured. Per-card Excel buttons are tagged to be left out of the image.
  exportPdf = async () => {
    if (!this.exportRef.current || this.state.exporting) return;
    this.setState({ exporting: true });
    try {
      await exportElementToPdf(this.exportRef.current, 'dashboard');
    } catch (e) {
      this.setState({ err: true });
    } finally {
      this.setState({ exporting: false });
    }
  }

  componentDidMount(): void {
    if (!this.props.token) {
      window.location.pathname = "/";
    }
    else {
      this.getDashboardData();
      this.getDashboardStats();
      // Change 91: poll dashboard for system-wide alerts (promo expired, generic email failures, etc.).
      // Order-related alerts are surfaced under each order row in the Orders tab and are refreshed
      // by that screen's own fetch, so the dashboard no longer needs to track them.
      this.dashboardPollTimer = setInterval(() => {
        if (!this.props.token) return;
        axios
          .get(`${baseURL}/dashboard`, { headers: { Authorization: `Bearer ${this.props.token}` } })
          .then((response: any) => {
            if (response?.data?.message === "success") {
              this.setState({ dashboardData: response?.data, err: false });
            }
          })
          .catch(() => {});
      }, 45000);
    }
  }

  componentWillUnmount(): void {
    if (this.dashboardPollTimer) {
      clearInterval(this.dashboardPollTimer);
      this.dashboardPollTimer = null;
    }
    if (tempTimeout) {
      clearTimeout(tempTimeout);
    }
  }

  getDashboardData = () => {
    if (!this.state.requested) {
      this.setState({ err: false, requested: true });
      axios.get(`${baseURL}/dashboard`,
        {
          headers: { Authorization: `Bearer ${this.props.token}` }
        })
        .then((response: any) => {
        //console.log(response);
          if (response?.data?.message === "success") {
            const doughnutChartCanvas = document.getElementById('transaction-history');
            new Chart(doughnutChartCanvas, {
              type: 'doughnut',
              data: {
                labels: ["Received", "Paid"],
                datasets: [{
                  data: [response?.data?.transactionData?.received || 0, response?.data?.transactionData?.paid || 0],
                  backgroundColor: [
                    "#00d25b", "#111111"
                  ],
                  borderColor: "#191c24"
                }]
              },
              options: {
                cutout: 70,
                animationEasing: "easeOutBounce",
                animateRotate: true,
                animateScale: false,
                responsive: true,
                maintainAspectRatio: true,
                showScale: false,
                legend: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                },
              },
            });

            this.initOwlCarousel();
            this.setState({ dashboardData: response?.data, requested: false, err: false })
          }
          else {
          //console.log("X")
            this.setState({ err: true, requested: false });
          }
        })
        .catch((error: any) => {
        //console.log(error);
          this.setState({ err: true, requested: false });
        })
    }
  }

  getDashboardStats = () => {
    axios.get(`${baseURL}/dashboard-stats`,
      {
        headers: { Authorization: `Bearer ${this.props.token}` }
      })
      .then((response: any) => {
        if (response?.data?.message === "success") {
          // Client-reported: "when i freshly open the dashboard the order
          // trend growth weekly is selected by default but not showing any
          // graph and when i click on monthly then back to weekly its
          // showing the data graph". Root cause: renderOrderGrowthChart()
          // reads `this.state.dashboardStats`, but React 18 batches setState
          // updates even inside promise callbacks — calling it right after
          // setState() (same tick) meant it was still reading the OLD state
          // (null) on the very first load, so trend.length was 0 and the
          // chart was skipped. Switching tabs "fixed" it by coincidence: that
          // handler already used setState's callback form, which is
          // guaranteed to run after the update lands. Moved this call into
          // that same callback form so it's never reading stale state.
          this.setState({ dashboardStats: response?.data }, () => {
            this.renderOrderGrowthChart();
            this.renderShootTypeChart();
          });

          const topCitiesCanvas = document.getElementById('top-cities-bar');
          const cities = response?.data?.topCities || [];
          if (topCitiesCanvas && cities.length) {
            new Chart(topCitiesCanvas, {
              type: 'bar',
              data: {
                labels: cities.map((c: any) => c.city),
                datasets: [{
                  label: 'Bookings',
                  data: cities.map((c: any) => c.count),
                  backgroundColor: "#3454d1",
                }]
              },
              options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: true,
                plugins: { legend: { display: false } },
                scales: { x: { ticks: { color: '#fff' } }, y: { ticks: { color: '#fff' } } },
              },
            });
          }
        }
      })
      .catch(() => {});
  }

  /** Redraws the Order Growth Trend bar chart for the currently-selected
   * period — called on initial stats fetch AND whenever the Weekly/Monthly/
   * Yearly toggle changes, so the existing Chart.js instance must be
   * destroyed first (Chart.js throws "Canvas is already in use" otherwise). */
  renderOrderGrowthChart = () => {
    const canvas: any = document.getElementById('order-growth-trend');
    const trend = this.state.dashboardStats?.orderGrowthTrend?.[this.state.orderTrendPeriod] || [];
    if (!canvas) return;
    if (this.orderTrendChartInstance) {
      this.orderTrendChartInstance.destroy();
      this.orderTrendChartInstance = null;
    }
    if (!trend.length) return;
    this.orderTrendChartInstance = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: trend.map((t: any) => t.label),
        datasets: [{
          label: 'Orders',
          data: trend.map((t: any) => t.count),
          backgroundColor: "#00d25b",
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: { legend: { display: false } },
        scales: { x: { ticks: { color: '#fff' } }, y: { ticks: { color: '#fff' }, beginAtZero: true } },
      },
    });
  }

  /** Redraws the Shoot Type Split pie chart for the currently-selected
   * period — client-reported: this card had no period filter at all
   * (always all-time), unlike every other dashboard card. Mirrors
   * renderOrderGrowthChart's destroy-then-recreate pattern. */
  renderShootTypeChart = () => {
    const canvas: any = document.getElementById('shoot-type-split');
    const split = this.state.dashboardStats?.shootTypeSplitByPeriod?.[this.state.shootTypePeriod] || [];
    if (!canvas) return;
    if (this.shootTypeChartInstance) {
      this.shootTypeChartInstance.destroy();
      this.shootTypeChartInstance = null;
    }
    if (!split.length) return;
    this.shootTypeChartInstance = new Chart(canvas, {
      type: 'pie',
      data: {
        labels: split.map((s: any) => s.label || s.type),
        datasets: [{
          data: split.map((s: any) => s.count),
          backgroundColor: ["#00d25b", "#fc424a", "#3454d1", "#ffab00", "#8965e0"],
          borderColor: "#191c24",
        }]
      },
      options: { responsive: true, maintainAspectRatio: true, plugins: { legend: { display: true, position: 'bottom', labels: { color: '#fff' } } } },
    });
  }

  toggleModal = () => {
    if (this.state.err) {
      this.setState({ err: false });
    }

    if (this.state.uploadImage) {
      this.setState({ uploadImage: false });
    }

    if (this.state.showConfirmation) {
      this.setState({ showConfirmation: false });
    }
  }

  onUploadBanner = (imageUrl: string, updateBannerFor: string) => {
    if (!this.state.requested && imageUrl && updateBannerFor) {
      this.setState({ err: false, requested: true, uploadImage: false });
      axios.post(`https://api.snapwiz.in/add-banner`,
        {
          imageUrl,
          updateBannerFor
        })
        .then((response: any) => {
        //console.log(response);
          if (response?.data?.success) {
            window.location.reload();
          }
          else {
            this.setState({ err: true, requested: false });
          }
        })
        .catch((error: any) => {
        //console.log(error);
          this.setState({ err: true, requested: false });
        })
    }
  }

  onRemoveBannerSnapmaster = (updateBannerFor: string = "snapmaster") => {
    let fileName = `${document?.querySelector(`.${updateBannerFor}_banner_owl_anchor .owl-item.active .item img`)?.src?.split("https://api.snapwiz.in/user_media/")?.[1]}`;
  //console.log(fileName, updateBannerFor);
    if (!this.state.requested && fileName && updateBannerFor) {
      this.setState({ err: false, requested: true });
      axios.post(`https://api.snapwiz.in/delete-user-media`,
        {
          fileName,
          updateBannerFor
        })
        .then((response: any) => {
        //console.log(response);
          if (response?.data?.success) {
            window.location.reload();
          }
          else {
            this.setState({ err: true, requested: false, showConfirmation: false });
          }
        })
        .catch((error: any) => {
        //console.log(error);
          this.setState({ err: true, requested: false, showConfirmation: false });
        })
    }
  }

  onRemoveBanner = (updateBannerFor: string = "snapwiz") => {
    let fileName = `${document?.querySelector(`.${updateBannerFor}_banner_owl_anchor .owl-item.active .item img`)?.src?.split("https://api.snapwiz.in/user_media/")?.[1]}`;
  //console.log(fileName, updateBannerFor);
    if (!this.state.requested && fileName && updateBannerFor) {
      this.setState({ err: false, requested: true });
      axios.post(`https://api.snapwiz.in/delete-user-media`,
        {
          fileName,
          updateBannerFor
        })
        .then((response: any) => {
        //console.log(response);
          if (response?.data?.success) {
            window.location.reload();
          }
          else {
            this.setState({ err: true, requested: false, showConfirmation: false });
          }
        })
        .catch((error: any) => {
        //console.log(error);
          this.setState({ err: true, requested: false, showConfirmation: false });
        })
    }
  }

  initOwlCarousel = () => {
    this.setState({ requested: true });
    tempTimeout = setTimeout(() => {
      if ($('#owl-carousel-basic').length) {
        $('#owl-carousel-basic').owlCarousel({
          loop: true,
          margin: 10,
          dots: false,
          nav: true,
          autoplay: false,
          autoplayTimeout: 4500,
          navText: ["<i class='mdi mdi-chevron-left'></i>", "<i class='mdi mdi-chevron-right'></i>"],
          responsive: {
            0: {
              items: 1
            },
            600: {
              items: 1
            },
            1000: {
              items: 1
            }
          }
        });
      }

      if ($('#owl-carousel-basic_1').length) {
        $('#owl-carousel-basic_1').owlCarousel({
          loop: true,
          margin: 10,
          dots: false,
          nav: true,
          autoplay: false,
          autoplayTimeout: 4500,
          navText: ["<i class='mdi mdi-chevron-left'></i>", "<i class='mdi mdi-chevron-right'></i>"],
          responsive: {
            0: {
              items: 1
            },
            600: {
              items: 1
            },
            1000: {
              items: 1
            }
          }
        });
      }

      if ($('#owl-carousel-rtl').length) {
        $('#owl-carousel-rtl').owlCarousel({
          loop: true,
          margin: 10,
          dots: false,
          nav: true,
          rtl: isrtl,
          autoplay: true,
          autoplayTimeout: 4500,
          navText: ["<i class='mdi mdi-chevron-right'></i>", "<i class='mdi mdi-chevron-left'></i>"],
          responsive: {
            0: {
              items: 1
            },
            600: {
              items: 1
            },
            1000: {
              items: 1
            }
          }
        });
      }

      this.setState({ requested: false });
    }, 1000);
  }

  handleChange = (e: any) => {
    const selectedFile = e.target.files[0];
    this.setState({ selectedFile });
  //console.log('Selected file:', selectedFile);
  };

  handleUpload = async (updateBannerFor: string = "snapwiz") => {
    if (!this.state.selectedFile) return alert('Please select a file first.');

    const formData = new FormData();
    formData.append('image', this.state.selectedFile); // 'file' is the field name the backend expects

    try {
      const response = await axios.post('https://api.snapwiz.in/upload/picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response?.data?.imageUrl) {
        this.onUploadBanner(response?.data?.imageUrl, updateBannerFor);
      }
      else {
        this.setState({ requested: false, err: true })
      }
    //console.log('Upload success:', response.data);
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  openRemoveConfirmation = (appName: string) => {
    if (!this.state.showConfirmation) {
      this.setState({ showConfirmation: appName });
    }
  }

  render() {
  //console.log(this.state.dashboardData)
    return (
      <AdminShell
        activePage="home"
        title="Dashboard"
        subtitle="Bookings, revenue, and platform health at a glance"
        topbarActions={
          <MDBBtn
            color='secondary'
            rippleColor='light'
            disabled={this.state.exporting || !this.state.dashboardStats}
            onClick={this.exportPdf}
            style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {this.state.exporting ? 'Exporting…' : 'Export as PDF'}
          </MDBBtn>
        }
      >
        <Modal open={!this.state.requested && (this.state.showConfirmation === "snapmaster")} secondaryText="No" primaryText="Yes" title='Remove Banner' onConfirm={this.onRemoveBannerSnapmaster} toggleOpen={this.toggleModal} content={<h6>Are you sure about removing this banner?</h6>} />
        <Modal open={!this.state.requested && (this.state.showConfirmation === "snapwiz")} secondaryText="No" primaryText="Yes" title='Remove Banner' onConfirm={this.onRemoveBanner} toggleOpen={this.toggleModal} content={<h6>Are you sure about removing this banner?</h6>} />
        <Modal open={!this.state.requested && this.state.uploadImage} hideSec primaryText="Upload" title='Upload Banner' onConfirm={() => { this.handleUpload(this.state.uploadImage) }} toggleOpen={this.toggleModal}
          content={
            <div>
              <MDBFile accept=".jpg,.jpeg,.png" label="Upload your file" id="customFile" onChange={this.handleChange} labelStyle={{ display: "none" }} style={{ height: 35 }} />
            </div>
          }
        />
        <Modal open={this.state.err} hideSec primaryText="Okay" title='Update' onConfirm={this.toggleModal} toggleOpen={this.toggleModal} content={<h6>Something went wrong, please try again later</h6>} />
        {this.state.requested ? <Loader /> : null}
        <div ref={this.exportRef} style={{ background: '#000' }}>
        {(() => {
                const stats = this.state.dashboardStats;
                const fmtMinutes = (m: number | null | undefined) => m == null ? '—' : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
                const kpiCardStyle: React.CSSProperties = {
                  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 14, padding: '16px 18px', flex: '1 1 220px', minWidth: 240,
                  display: 'flex', flexDirection: 'column', gap: 10,
                };
                const kpiHeadRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 };
                const kpiLabelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 700, opacity: 0.65 };
                const kpiValueStyle: React.CSSProperties = { fontSize: 26, fontWeight: 800, letterSpacing: '-0.01em' };
                // Per-card Excel export — sends the card's own selected period so the file matches what it shows.
                const sectionExport = (section: string, period?: string) => (
                  <ExportButton small endpoint="/export/dashboard-section" label="Excel" extraQuery={`section=${section}${period ? `&period=${period}` : ''}`} />
                );

                const kpi = stats?.kpi;
                const bp = this.state.bookingsPeriod, rp = this.state.revenuePeriod, mp = this.state.matchPeriod, pm = this.state.photographerMetric;

                const bookingsValue = kpi?.bookings?.[bp];
                const bookingsPrev = kpi?.bookings?.[PREV_KEY_FOR[bp]];
                const bookingsDelta = (bookingsValue != null && bookingsPrev != null) ? pctDelta(bookingsValue, bookingsPrev) : null;

                const revenueValue = kpi?.revenueGross?.[rp];
                const revenuePrev = kpi?.revenueGross?.[PREV_KEY_FOR[rp]];
                const revenueDelta = (revenueValue != null && revenuePrev != null) ? pctDelta(revenueValue, revenuePrev) : null;
                const revenueNetValue = kpi?.revenueNet?.[rp];

                const matchValue = kpi?.avgMatchMinutes?.[mp];
                const matchPrev = kpi?.avgMatchMinutes?.[MATCH_PREV_KEY_FOR[mp]];
                const matchDiffMinutes = (matchValue != null && matchPrev != null) ? matchValue - matchPrev : null;

                const photographerValueFor: Record<PhotographerMetricKey, number | undefined> = {
                  total: stats?.photographerCounts?.total, live: stats?.photographerCounts?.live,
                  new: stats?.photographerCounts?.newToday, removed: stats?.photographerCounts?.blocked,
                };
                const photographerLabelFor: Record<PhotographerMetricKey, string> = { total: 'total', live: 'verified & active', new: 'new today', removed: 'blocked' };

                return (
                  <>
                    {/* ===================== KPI row (admin-dashboard.html reference: bookings/revenue/photographers/avg-match-time) ===================== */}
                    <div className="row">
                      <div className="col-12 grid-margin">
                        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                          <div style={kpiCardStyle}>
                            <div style={kpiHeadRowStyle}>
                              <div style={kpiLabelStyle}>Total bookings<div style={{ fontSize: 10.5, opacity: 0.5, fontWeight: 500 }}>&gt; Orders</div></div>
                              {sectionExport('bookings', bp)}
                            </div>
                            <SegmentedControl
                              value={bp}
                              onChange={(v) => this.setState({ bookingsPeriod: v })}
                              options={[{ key: 'today', label: 'Today' }, { key: 'week', label: 'Weekly' }, { key: 'month', label: 'Monthly' }, { key: 'year', label: 'Yearly' }]}
                            />
                            <div style={kpiValueStyle}>{bookingsValue ?? '—'}</div>
                            <DeltaBadge
                              hidden={!kpi}
                              deltaLabel={bookingsDelta == null ? null : `${bookingsDelta >= 0 ? '▲' : '▼'} ${Math.abs(bookingsDelta)}% ${VS_LABEL_FOR[bp]}`}
                              isGood={bookingsDelta == null ? null : bookingsDelta >= 0}
                            />
                          </div>

                          <div style={kpiCardStyle}>
                            <div style={kpiHeadRowStyle}>
                              <div style={kpiLabelStyle}>Gross &amp; net revenue<div style={{ fontSize: 10.5, opacity: 0.5, fontWeight: 500 }}>&gt; Revenue</div></div>
                              {sectionExport('revenue', rp)}
                            </div>
                            <SegmentedControl
                              value={rp}
                              onChange={(v) => this.setState({ revenuePeriod: v })}
                              options={[{ key: 'today', label: 'Today' }, { key: 'week', label: 'Weekly' }, { key: 'month', label: 'Monthly' }, { key: 'year', label: 'Yearly' }]}
                            />
                            <div style={{ display: 'flex', gap: 18 }}>
                              <div>
                                <div style={{ fontSize: 10.5, opacity: 0.55, fontWeight: 700 }}>Gross</div>
                                <div style={kpiValueStyle}>{revenueValue != null ? `₹${revenueValue.toLocaleString('en-IN')}` : '—'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10.5, opacity: 0.55, fontWeight: 700 }}>Net</div>
                                <div style={kpiValueStyle}>{revenueNetValue != null ? `₹${revenueNetValue.toLocaleString('en-IN')}` : '—'}</div>
                              </div>
                            </div>
                            <DeltaBadge
                              hidden={!kpi}
                              deltaLabel={revenueDelta == null ? null : `${revenueDelta >= 0 ? '▲' : '▼'} ${Math.abs(revenueDelta)}% ${VS_LABEL_FOR[rp]}`}
                              isGood={revenueDelta == null ? null : revenueDelta >= 0}
                            />
                          </div>

                          <div style={kpiCardStyle}>
                            <div style={kpiHeadRowStyle}>
                              <div style={kpiLabelStyle}>Photographers<div style={{ fontSize: 10.5, opacity: 0.5, fontWeight: 500 }}>&gt; Total / Live / New / Removed</div></div>
                              {sectionExport('photographers', pm)}
                            </div>
                            <SegmentedControl
                              value={pm}
                              onChange={(v) => this.setState({ photographerMetric: v })}
                              options={[{ key: 'total', label: 'Total' }, { key: 'live', label: 'Live' }, { key: 'new', label: 'New' }, { key: 'removed', label: 'Removed' }]}
                            />
                            <div style={kpiValueStyle}>{photographerValueFor[pm] ?? '—'}</div>
                            <span style={{ fontSize: 11, opacity: 0.5, fontWeight: 600 }}>
                              {photographerLabelFor[pm]}{stats?.asOf ? ` · as of ${moment(stats.asOf).format('h:mm A')}` : ''}
                            </span>
                          </div>

                          <div style={kpiCardStyle}>
                            <div style={kpiHeadRowStyle}>
                              <div style={kpiLabelStyle}>Avg. time to match<div style={{ fontSize: 10.5, opacity: 0.5, fontWeight: 500 }}>&gt;&gt;&gt; booking → photographer assigned</div></div>
                              {sectionExport('matchtime', mp)}
                            </div>
                            <SegmentedControl
                              value={mp}
                              onChange={(v) => this.setState({ matchPeriod: v })}
                              options={[{ key: 'week', label: 'Weekly' }, { key: 'month', label: 'Monthly' }, { key: 'year', label: 'Yearly' }]}
                            />
                            <div style={kpiValueStyle}>{fmtMinutes(matchValue)}</div>
                            <DeltaBadge
                              hidden={!kpi}
                              deltaLabel={matchDiffMinutes == null ? null : `${matchDiffMinutes <= 0 ? '▼' : '▲'} ${fmtMinutes(Math.abs(matchDiffMinutes))} ${MATCH_VS_LABEL_FOR[mp]}`}
                              isGood={matchDiffMinutes == null ? null : matchDiffMinutes <= 0}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Client-reported: Ongoing Orders should sit directly below the top
                        metric section, and the new Order Growth Trend chart goes right
                        below that — moved up from further down the page. */}
                    <div className="row">
                      <div className="col-md-4 grid-margin stretch-card">
                        <div className="card">
                          <div className="card-body">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                              <h4 className="card-title" style={{ marginBottom: 0 }}>Transaction Statistic</h4>
                              {sectionExport('transactions')}
                            </div>
                            <div className="position-relative">
                              <div className="daoughnutchart-wrapper">
                                <canvas id="transaction-history" className="transaction-chart"></canvas>
                              </div>
                              <div className="custom-value">₹ {(this.state.dashboardData?.transactionData?.received + this.state.dashboardData?.transactionData?.paid) || 0}<span>Total</span>
                              </div>
                            </div>
                            <div className="bg-gray-dark d-flex d-md-block d-xl-flex flex-row py-3 px-4 px-md-3 px-xl-4 rounded mt-3">
                              <div className="text-md-center text-xl-left">
                                <h6 className="mb-1" style={{ textAlign: 'left' }}>Transfer to You</h6>
                                <p className="text-muted mb-0" style={{ textAlign: 'left' }}>Till Date</p>
                              </div>
                              <div className="align-self-center flex-grow text-end text-md-right text-xl-right py-md-2 py-xl-0">
                                <h6 className="font-weight-bold mb-0" id="__receivedAmt">₹ {this.state.dashboardData?.transactionData?.received || 0}</h6>
                              </div>
                            </div>
                            <div className="bg-gray-dark d-flex d-md-block d-xl-flex flex-row py-3 px-4 px-md-3 px-xl-4 rounded mt-3">
                              <div className="text-md-center text-xl-left">
                                <h6 className="mb-1" style={{ textAlign: 'left' }}>Payment to Partners</h6>
                                <p className="text-muted mb-0" style={{ textAlign: 'left' }}>Till Date</p>
                              </div>
                              <div className="align-self-center flex-grow text-end text-md-right text-xl-right py-md-2 py-xl-0">
                                <h6 className="font-weight-bold mb-0">₹ {this.state.dashboardData?.transactionData?.paid || 0}</h6>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-8 grid-margin stretch-card">
                        <div className="card">
                          <div className="card-body">
                            <div className="d-flex flex-row justify-content-between">
                              <h4 className="card-title mb-1">Live bookings</h4>
                            </div>
                            {this.state.dashboardData?.ongoingOrders?.length ? (
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ textAlign: 'left', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, opacity: 0.55 }}>
                                      <th style={{ padding: '6px 10px' }}>Category</th>
                                      <th style={{ padding: '6px 10px' }}>Zone</th>
                                      <th style={{ padding: '6px 10px' }}>Slot</th>
                                      <th style={{ padding: '6px 10px' }}>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {this.state.dashboardData.ongoingOrders.map((item: any, idx: number) => (
                                      <tr
                                        key={item?.receipt_id || idx}
                                        onClick={() => { window.location.href = "/orders?sortBy=ongoing" }}
                                        style={{ cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.06)' }}
                                      >
                                        <td style={{ padding: '10px' }}>{item?.categoryData?.name || '—'}</td>
                                        <td style={{ padding: '10px', opacity: 0.75 }}>
                                          {item?.zone || '—'}
                                        </td>
                                        <td style={{ padding: '10px', opacity: 0.75 }}>{item?.eventTime?.from ? moment(new Date(item.eventTime.from)).format('MMM D, h:mm a') : '—'}</td>
                                        <td style={{ padding: '10px' }}>
                                          <span style={{
                                            fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 20, textTransform: 'capitalize',
                                            background: 'rgba(255,255,255,0.08)',
                                          }}>{item?.status === 'paid' ? 'Upcoming' : (item?.status || '—')}</span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="d-flex flex-row justify-content-center align-items-center" style={{ height: 120 }}>
                                <p className="card-title mb-1" style={{ padding: "5px 20px", backgroundColor: "#444", borderRadius: 5, userSelect: 'none' }}>No ongoing orders</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 grid-margin stretch-card">
                        <div className="card">
                          <div className="card-body">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                              <h4 className="card-title" style={{ marginBottom: 0 }}>Order Growth Trend</h4>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {sectionExport('ordertrend', this.state.orderTrendPeriod)}
                              <SegmentedControl
                                value={this.state.orderTrendPeriod}
                                onChange={(v) => this.setState({ orderTrendPeriod: v }, () => this.renderOrderGrowthChart())}
                                options={[{ key: 'week', label: 'Weekly' }, { key: 'month', label: 'Monthly' }, { key: 'year', label: 'Yearly' }]}
                              />
                              </div>
                            </div>
                            <canvas id="order-growth-trend" style={{ maxHeight: 260, marginTop: 10 }}></canvas>
                            {!stats?.orderGrowthTrend?.[this.state.orderTrendPeriod]?.length ? <div style={{ fontSize: 13, opacity: 0.6, marginTop: 8 }}>No data yet</div> : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 grid-margin stretch-card">
                        <div className="card">
                          <div className="card-body">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                              <h4 className="card-title" style={{ marginBottom: 0 }}>Shoot Type Split</h4>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              {sectionExport('shoottype', this.state.shootTypePeriod)}
                              <SegmentedControl
                                value={this.state.shootTypePeriod}
                                onChange={(v) => this.setState({ shootTypePeriod: v }, () => this.renderShootTypeChart())}
                                options={[{ key: 'month', label: '1M' }, { key: 'quarter', label: '3M' }, { key: 'year', label: '1Y' }]}
                              />
                              </div>
                            </div>
                            <canvas id="shoot-type-split" style={{ maxHeight: 260, marginTop: 10 }}></canvas>
                            {!stats?.shootTypeSplitByPeriod?.[this.state.shootTypePeriod]?.length ? <div style={{ fontSize: 13, opacity: 0.6, marginTop: 8 }}>No data yet</div> : null}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 grid-margin stretch-card">
                        <div className="card">
                          <div className="card-body">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                              <h4 className="card-title" style={{ marginBottom: 0 }}>Top Cities/Zones by Bookings</h4>
                              {sectionExport('topzones')}
                            </div>
                            <canvas id="top-cities-bar" style={{ maxHeight: 260 }}></canvas>
                            {!stats?.topCities?.length ? <div style={{ fontSize: 13, opacity: 0.6, marginTop: 8 }}>No data yet</div> : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6 grid-margin stretch-card">
                        <div className="card">
                          <div className="card-body">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                              <h4 className="card-title" style={{ marginBottom: 0 }}>Most-Booked Photographers</h4>
                              {sectionExport('topphotographers')}
                            </div>
                            {stats?.mostBookedPhotographers?.length ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {stats.mostBookedPhotographers.map((p: any) => (
                                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                    <span>{p.name}</span>
                                    <span style={{ opacity: 0.8 }}>{p.count} bookings</span>
                                  </div>
                                ))}
                              </div>
                            ) : <div style={{ fontSize: 13, opacity: 0.6 }}>No data yet</div>}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6 grid-margin stretch-card">
                        <div className="card">
                          <div className="card-body">
                            <h4 className="card-title">Recent Reviews</h4>
                            {stats?.recentReviews?.length ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {stats.recentReviews.map((r: any, idx: number) => (
                                  <div key={idx} style={{ fontSize: 13 }}>
                                    <span style={{ color: '#FFB020' }}>{'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}</span>
                                    {' '}
                                    <span style={{ opacity: 0.85 }}>{r.comment || '(no comment)'}</span>
                                    <div style={{ fontSize: 11, opacity: 0.6 }}>{r.photographer_name || 'Unknown photographer'} · #{r.receipt_id}</div>
                                  </div>
                                ))}
                              </div>
                            ) : <div style={{ fontSize: 13, opacity: 0.6 }}>No reviews yet</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
              {/* ===================== Change 80: Admin Alerts Section ===================== */}
              <div className="row">
                <div className="col-12 grid-margin stretch-card">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex flex-row justify-content-between align-items-center" style={{ marginBottom: 16 }}>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <i className="mdi mdi-bell-outline" style={{ fontSize: 18, color: '#9e9e9e' }}></i>
                          <h4 className="card-title mb-0">Alerts & Notifications</h4>
                          {/* Change 91: clarify scope — order-related alerts now live under each order in the Orders tab. */}
                          <span style={{ fontSize: 11, color: '#666', marginLeft: 6 }}>(system-wide only · order alerts are under each order in the Orders tab)</span>
                        </div>
                        {this.state.dashboardData?.adminAlerts?.length > 0 && (
                          <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <span style={{ fontSize: 12, color: '#9e9e9e', backgroundColor: 'rgba(255,255,255,0.06)', padding: '3px 10px', borderRadius: 12 }}>
                              {this.state.dashboardData.adminAlerts.length} active
                            </span>
                            {/* ===================== Change 84: Resolve All button ===================== */}
                            <button
                              style={{
                                fontSize: 11, padding: '3px 12px', borderRadius: 4,
                                border: '1px solid rgba(192,120,120,0.3)',
                                backgroundColor: 'rgba(192,120,120,0.1)',
                                color: '#c07878', cursor: 'pointer', fontWeight: 600,
                              }}
                              onMouseEnter={e => { (e.target as any).style.backgroundColor = 'rgba(192,120,120,0.2)'; }}
                              onMouseLeave={e => { (e.target as any).style.backgroundColor = 'rgba(192,120,120,0.1)'; }}
                              onClick={() => {
                                if (this.state.requested) return;
                                if (!window.confirm('Resolve all active alerts?')) return;
                                this.setState({ requested: true });
                                axios.post(`${baseURL}/resolve-all-alerts`, {}, {
                                  headers: { Authorization: `Bearer ${this.props.token}` }
                                }).then(() => {
                                  window.location.reload();
                                }).catch(() => {
                                  this.setState({ requested: false, err: true });
                                });
                              }}
                            >
                              Resolve All
                            </button>
                            {/* ===================== END Change 84 ===================== */}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {this.state.dashboardData?.adminAlerts?.length ? this.state.dashboardData.adminAlerts.map((alert: any) => {
                          const isHigh = alert?.priority === 'high';
                          const isMed = alert?.priority === 'medium';
                          const accentColor = isHigh ? '#c07878' : isMed ? '#b89a5a' : '#6a92b8';
                          const rowBg = isHigh ? 'rgba(192,120,120,0.07)' : isMed ? 'rgba(184,154,90,0.07)' : 'rgba(106,146,184,0.07)';
                          const badgeBg = isHigh ? 'rgba(192,120,120,0.15)' : isMed ? 'rgba(184,154,90,0.15)' : 'rgba(106,146,184,0.15)';
                          // ===================== Change 83: Added camera icon for selfie alerts =====================
                          // Old icon mapping:
                          // const iconName = alert?.type === 'photographer_cancel' ? 'mdi-account-off-outline' : alert?.type === 'radius_expansion' ? 'mdi-map-marker-radius-outline' : 'mdi-alert-outline';
                          const iconName = alert?.type === 'photographer_cancel' ? 'mdi-account-off-outline' : (alert?.type === 'radius_expansion' || alert?.type === 'v2_radius_expansion') ? 'mdi-map-marker-radius-outline' : alert?.type === 'start_selfie_submitted' ? 'mdi-camera-outline' : alert?.type === 'no_otp_before_shoot' ? 'mdi-timer-alert-outline' : alert?.type === 'photo_upload_link_received' ? 'mdi-cloud-upload-outline' : 'mdi-alert-outline';
                          // Change 83: Backend base URL for selfie images — toggle for local dev vs prod
                          // const BACKEND_MEDIA_BASE = 'https://api.snapwiz.in'; // Local Wi‑Fi: 'http://192.168.1.114:3001' | hotspot: 'http://10.118.156.10:3001'
                          // Change 84: Cloudinary URLs are full https:// — only prefix for old local paths
                          const BACKEND_MEDIA_BASE = 'https://api.snapwiz.in'; // Local dev Wi‑Fi: 'http://192.168.1.114:3001' (hotspot: 'http://10.118.156.10:3001')
                          const selfieFullUrl = alert?.selfie_url?.startsWith('http') ? alert.selfie_url : `${BACKEND_MEDIA_BASE}${alert.selfie_url}`;
                          return (
                            <div key={alert?._id} style={{
                              backgroundColor: rowBg,
                              borderLeft: `3px solid ${accentColor}`,
                              borderRadius: 6,
                              padding: '12px 16px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 14,
                            }}>
                              {/* Icon */}
                              <div style={{
                                width: 32, height: 32, borderRadius: 6,
                                backgroundColor: badgeBg,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0, marginTop: 2,
                              }}>
                                <i className={`mdi ${iconName}`} style={{ fontSize: 16, color: accentColor }}></i>
                              </div>
                              {/* Content */}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#d4d4d4', lineHeight: 1.4 }}>{alert?.title}</p>
                                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#858585', lineHeight: 1.5 }}>{alert?.message}</p>
                                {alert?.order_receipt_id && (
                                  <p style={{ margin: '4px 0 0', fontSize: 11, color: '#666', fontFamily: 'monospace' }}>#{alert?.order_receipt_id}</p>
                                )}
                                {/* ===================== Change 83: Selfie image + GPS + timestamp display ===================== */}
                                {alert?.type === 'start_selfie_submitted' && alert?.selfie_url && (
                                  <div style={{ marginTop: 8 }}>
                                    <img
                                      src={selfieFullUrl}
                                      alt="Start selfie"
                                      style={{ width: 100, height: 100, borderRadius: 8, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
                                      onClick={() => window.open(selfieFullUrl, '_blank')}
                                    />
                                    <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                      {alert?.selfie_submitted_at && (
                                        <p style={{ margin: 0, fontSize: 11, color: '#858585' }}>
                                          <i className="mdi mdi-clock-outline" style={{ fontSize: 11, marginRight: 4 }}></i>
                                          {moment(new Date(alert.selfie_submitted_at)).format('DD MMM YYYY, hh:mm:ss A')}
                                        </p>
                                      )}
                                      {/* Change 84: Fix "0" rendering bug — use != null instead of || */}
                                      {/* Old: {(alert?.selfie_lat || alert?.selfie_lng) && ( */}
                                      {(alert?.selfie_lat != null || alert?.selfie_lng != null) && (
                                        <p style={{ margin: 0, fontSize: 11, color: '#858585' }}>
                                          <i className="mdi mdi-map-marker-outline" style={{ fontSize: 11, marginRight: 4 }}></i>
                                          {Number(alert.selfie_lat).toFixed(6)}, {Number(alert.selfie_lng).toFixed(6)}
                                          <a
                                            href={`https://www.google.com/maps?q=${alert.selfie_lat},${alert.selfie_lng}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ marginLeft: 6, fontSize: 10, color: '#6a92b8', textDecoration: 'none' }}
                                          >
                                            View on Map ↗
                                          </a>
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                                {/* ===================== END Change 83 ===================== */}
                              </div>
                              {/* Right side */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0, minWidth: 90 }}>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, letterSpacing: '0.5px',
                                  textTransform: 'uppercase', color: accentColor,
                                  backgroundColor: badgeBg, padding: '2px 8px', borderRadius: 10,
                                }}>
                                  {alert?.priority}
                                </span>
                                <p style={{ margin: 0, fontSize: 11, color: '#666' }}>{moment(new Date(alert?.createdAt)).fromNow()}</p>
                                {!alert?.resolved && (
                                  <button
                                    style={{
                                      fontSize: 11, padding: '3px 10px', borderRadius: 4,
                                      border: `1px solid rgba(255,255,255,0.12)`,
                                      backgroundColor: 'rgba(255,255,255,0.05)',
                                      color: '#9e9e9e', cursor: 'pointer', marginTop: 2,
                                    }}
                                    onMouseEnter={e => { (e.target as any).style.backgroundColor = 'rgba(255,255,255,0.1)'; (e.target as any).style.color = '#d4d4d4'; }}
                                    onMouseLeave={e => { (e.target as any).style.backgroundColor = 'rgba(255,255,255,0.05)'; (e.target as any).style.color = '#9e9e9e'; }}
                                    onClick={() => {
                                      if (this.state.requested) return;
                                      this.setState({ requested: true });
                                      axios.post(`${baseURL}/resolve-alert`, { alertId: alert?._id }, {
                                        headers: { Authorization: `Bearer ${this.props.token}` }
                                      }).then(() => {
                                        window.location.reload();
                                      }).catch(() => {
                                        this.setState({ requested: false, err: true });
                                      });
                                    }}
                                  >
                                    Resolve
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        }) : (
                          <div className="d-flex flex-row justify-content-center align-items-center" style={{ height: 72 }}>
                            <p className="card-title mb-1" style={{ padding: "5px 20px", backgroundColor: "rgba(255,255,255,0.04)", borderRadius: 5, userSelect: 'none', color: '#666', fontSize: 13 }}>No active alerts</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* ===================== END Change 80 ===================== */}
              <div className="row">
                <div className="col-md-6 col-xl-4 grid-margin stretch-card">
                  <div className="card">
                    <div className="card-body" style={{ minHeight: 400 }}>
                      <div className="d-flex flex-row justify-content-between">
                        <h4 className="card-title">Feedback</h4>
                        <p className="text-primary mb-1 small" style={{ cursor: 'pointer' }} onClick={() => { window.location.pathname = "/feedback" }}>View all</p>
                      </div>
                      <div className="preview-list">
                        {this.state.dashboardData?.feedbacks?.length ? this.state.dashboardData.feedbacks.map((item: any) => {
                          return <div className="preview-item border-bottom">
                            <div className="preview-thumbnail" style={{ alignSelf: 'center' }}>
                              <svg width="24" height="24" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" mirror-in-rtl="true" fill="#FFFFFF" stroke="#FFFFFF">
                                <g id="SVGRepo_bgCarrier" stroke-width="0" />
                                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                                <g id="SVGRepo_iconCarrier"> <path fill="#FFFFFF" d="M4.1 2h-.2A2.906 2.906 0 0 0 1 4.9v1.2A2.906 2.906 0 0 0 3.9 9h.2A2.906 2.906 0 0 0 7 6.1V4.9A2.906 2.906 0 0 0 4.1 2zM4 10a4.012 4.012 0 0 0-4 4v2.667a1.326 1.326 0 0 0 1.333 1.324l5.333.01A1.337 1.337 0 0 0 8 16.667V14a4.01 4.01 0 0 0-4-4zM18 1v4a1 1 0 0 1-1 1h-3.99l-1.19 1.88a.47.47 0 0 1-.32.12.538.538 0 0 1-.21-.05.493.493 0 0 1-.29-.45V6h-1a1 1 0 0 1-1-1V1a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1z" /> </g>
                              </svg>
                              {/* <img src="assets/images/faces/face6.jpg" alt="image" className="rounded-circle" /> */}
                            </div>
                            <div className="preview-item-content d-flex flex-grow">
                              <div className="flex-grow">
                                <div className="d-flex d-md-block d-xl-flex justify-content-between">
                                  <h6 className="preview-subject">{item?.error_details?.error_title}</h6>
                                  <p className="text-muted text-small">{item?.createdAt ? moment(new Date(item?.createdAt)).fromNow() : null}</p>
                                </div>
                                <p className="text-muted">{item?.error_details?.error_desc}</p>
                              </div>
                            </div>
                          </div>
                        }) : <div className="d-flex flex-row justify-content-center align-items-center" style={{ height: '100%' }}>
                          <p className="card-title mb-1" style={{ padding: "5px 20px", backgroundColor: "#444", borderRadius: 5, userSelect: 'none' }}>No data</p>
                        </div>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4 grid-margin stretch-card">
                  <div className="card">
                    <div className="card-body" style={{ minHeight: 400 }}>
                      <div className="d-flex flex-row justify-content-between">
                        <h4 className="card-title">Reports</h4>
                        <p className="text-primary mb-1 small" style={{ cursor: 'pointer' }} onClick={() => { window.location.pathname = "/feedback" }}>View all</p>
                      </div>
                      <div className="preview-list">
                        {this.state.dashboardData?.reports?.length ? this.state.dashboardData.reports.map((item: any) => {
                          return <div className="preview-item border-bottom">
                            <div className="preview-thumbnail" style={{ alignSelf: "center" }}>
                              <svg fill="#FFFFFF" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 52 52" enable-background="new 0 0 52 52" stroke="#FFFFFF">
                                <g id="SVGRepo_bgCarrier" stroke-width="0" />
                                <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                                <g id="SVGRepo_iconCarrier"> <g> <path d="M22.8,45.7v1c0,1.2-0.9,2.1-2.1,2.1h0H4.1c-1.2,0-2.1-0.9-2.1-2.1l0,0v-1c0-2.5,3-4.1,5.7-5.3 c0.1,0,0.2-0.1,0.3-0.2c0.2-0.1,0.5-0.1,0.7,0c1.1,0.7,2.4,1.1,3.8,1.1c1.3,0,2.6-0.4,3.8-1.1c0.2-0.1,0.4-0.1,0.6,0 c0.1,0,0.2,0.1,0.3,0.2C19.9,41.7,22.8,43.2,22.8,45.7z" /> <ellipse cx="12.4" cy="33.7" rx="5.2" ry="5.7" /> <path d="M34.8,3.2L34.8,3.2c-8.5,0-15.3,6.5-15.3,14.5c0,2.5,0.7,5,2,7.2c0.1,0.2,0.2,0.5,0.2,0.8L20,30.3 c-0.2,0.6,0.2,1.1,0.7,1.3c0.2,0.1,0.4,0.1,0.6,0l4.5-1.6c0.3-0.1,0.6-0.1,0.8,0.1c2.4,1.4,5.2,2.2,8,2.2c8.5,0,15.3-6.6,15.3-14.6 C50,9.7,43.1,3.2,34.8,3.2z M33.7,8.9h3v7l-0.3,4.6H34l-0.2-4.6V8.9z M35.2,26.2c-1.4,0-1.8-0.8-1.8-1.8c0-1,0.4-1.8,1.8-1.8 c1.4,0,1.8,0.8,1.8,1.8C37.1,25.4,36.6,26.2,35.2,26.2z" /> </g> </g>
                              </svg>
                              {/* <img src="assets/images/faces/face6.jpg" alt="image" className="rounded-circle" /> */}
                            </div>
                            <div className="preview-item-content d-flex flex-grow">
                              <div className="flex-grow">
                                <div className="d-flex d-md-block d-xl-flex justify-content-between">
                                  <h6 className="preview-subject">{item?.error_details?.error_title}</h6>
                                  <p className="text-muted text-small">{item?.createdAt ? moment(new Date(item?.createdAt)).fromNow() : null}</p>
                                </div>
                                <p className="text-muted">{item?.error_details?.error_desc}</p>
                              </div>
                            </div>
                          </div>
                        }) : <div className="d-flex flex-row justify-content-center align-items-center" style={{ height: '100%' }}>
                          <p className="card-title mb-1" style={{ padding: "5px 20px", backgroundColor: "#444", borderRadius: 5, userSelect: 'none' }}>No data</p>
                        </div>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4 grid-margin stretch-card">
                  <div className="card">
                    <div className="card-body" style={{ minHeight: 400 }}>
                      <h4 className="card-title">Upload Banner (SnapWiz)</h4>
                      <div className="owl-carousel owl-theme full-width owl-carousel-dash portfolio-carousel snapwiz_banner_owl_anchor"
                        id="owl-carousel-basic" style={{ maxHeight: 260 }}>
                        {this.state.dashboardData?.miscData?.bannerAds?.length ? this.state.dashboardData?.miscData?.bannerAds.map((item: any) => {
                          return <div className="item">
                            {/* <img src={`${baseURL}${item}`} alt="Rectangle" /> */}
                            <img src={`https://api.snapwiz.in${item}`} alt="Rectangle" style={{ height: "100%", width: "100%", maxHeight: 260, objectFit: 'contain' }} />
                          </div>
                        }) : null}
                      </div>
                      <div style={{ display: 'flex', gap: '25px', margin: '10px 0' }}>
                        <p className="text-primary mb-1 small" onClick={() => { this.setState({ uploadImage: "snapwiz" }) }} style={{ cursor: 'pointer' }}>Upload</p>
                        {document.querySelector(".snapwiz_banner_owl_anchor .owl-item.active .item img") && <p className="text-danger mb-1 small" onClick={() => { this.openRemoveConfirmation("snapwiz") }} style={{ cursor: 'pointer' }}>Remove</p>}
                      </div>
                      {/* <div className="progress progress-md portfolio-progress">
                    <div className="progress-bar bg-success" role="progressbar" style={{ width: '50%' }} aria-valuenow="25"
                      aria-valuemin="0" aria-valuemax="100" />
                  </div> */}
                    </div>
                  </div>
                </div>
                <div className="col-md-6 col-xl-4 grid-margin stretch-card">
                  <div className="card">
                    <div className="card-body" style={{ minHeight: 400 }}>
                      <h4 className="card-title">Upload Banner (SnapMaster)</h4>
                      <div className="owl-carousel owl-theme full-width owl-carousel-dash portfolio-carousel snapmaster_banner_owl_anchor"
                        id="owl-carousel-basic_1" style={{ maxHeight: 260 }}>
                        {this.state.dashboardData?.miscData?.bannerAdsSnapMaster?.length ? this.state.dashboardData?.miscData?.bannerAdsSnapMaster.map((item: any) => {
                          return <div className="item">
                            {/* <img src={`${baseURL}${item}`} alt="Rectangle" /> */}
                            <img src={`https://api.snapwiz.in${item}`} alt="Rectangle" style={{ height: "100%", width: "100%", maxHeight: 260, objectFit: 'contain' }} />
                          </div>
                        }) : null}
                      </div>
                      <div style={{ display: 'flex', gap: '25px', margin: '10px 0' }}>
                        <p className="text-primary mb-1 small" onClick={() => { this.setState({ uploadImage: "snapmaster" }) }} style={{ cursor: 'pointer' }}>Upload</p>
                        {document.querySelector(".snapmaster_banner_owl_anchor .owl-item.active .item img") ? <p className="text-danger mb-1 small" onClick={() => { this.openRemoveConfirmation("snapmaster") }} style={{ cursor: 'pointer' }}>Remove</p> : null}
                      </div>
                      {/* <div className="progress progress-md portfolio-progress">
                    <div className="progress-bar bg-success" role="progressbar" style={{ width: '50%' }} aria-valuenow="25"
                      aria-valuemin="0" aria-valuemax="100" />
                  </div> */}
                    </div>
                  </div>
                </div>
              </div>
        </div>
      </AdminShell>
    )
  }
}

const mapDispatchToProps = (dispatch: Function) => {
  return {
    dispatch
  }
}

const mapStateToProps = (state: any, ownProps: any) => {
  return {
    token: state.auth.token
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
